// ─── ABA: ANEXOS ───────────
// Briefings, KMZ/KML, contratos do cliente, fotos do local, etc.
// Upload via presigned URL (S3 direto, sem passar pelo APIGW).
// Onda 2 (2026-06-02): migrado pra Antd — Upload + Table + Tag.
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Table, Button, Space, Typography, Empty, Spin, App as AntApp, Upload, Popconfirm,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadProps } from 'antd';
import { PaperClipOutlined, DownloadOutlined, DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import type { Anexo } from './types';
import { ICONES_TIPO, fmtBytes, fmtDate } from './utils';

export function AbaAnexos({ negocioId, reloadFlag }: { negocioId: number; reloadFlag: number }) {
  const { message } = AntApp.useApp();
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState<string | null>(null);

  useEffect(() => { carregar(); }, [negocioId, reloadFlag]);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'anexos_list', negocio_id: negocioId },
        { headers: { Authorization: token } });
      const data = r.data?.body ? JSON.parse(r.data.body) : r.data;
      setAnexos(Array.isArray(data) ? data : []);
    } catch (e: any) {
      message.error('Erro carregar anexos: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }

  async function uploadArquivo(file: File): Promise<boolean> {
    if (file.size > 50 * 1024 * 1024) {
      message.warning(`"${file.name}" passa de 50MB e foi ignorado.`);
      return false;
    }
    setEnviando(file.name);
    try {
      const token = await getToken();
      // 1) Pega presigned URL
      const r1 = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'anexos_upload_url', negocio_id: negocioId,
          filename: file.name, content_type: file.type || 'application/octet-stream' },
        { headers: { Authorization: token } });
      const data1 = r1.data?.body ? JSON.parse(r1.data.body) : r1.data;
      const { upload_url, s3_key } = data1;
      // 2) PUT direto no S3
      await axios.put(upload_url, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        timeout: 300000,
      });
      // 3) Cria registro no banco
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'anexos_criar', negocio_id: negocioId,
          nome: file.name, s3_key, tamanho_bytes: file.size },
        { headers: { Authorization: token } });
      return true;
    } catch (e: any) {
      message.error(`Falha em "${file.name}": ` + (e.response?.data?.error || e.message));
      return false;
    } finally { setEnviando(null); }
  }

  async function baixar(a: Anexo) {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'anexos_download_url', id: a.id },
        { headers: { Authorization: token } });
      const data = r.data?.body ? JSON.parse(r.data.body) : r.data;
      window.open(data.download_url, '_blank');
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  async function excluir(a: Anexo) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'anexos_delete', id: a.id },
        { headers: { Authorization: token } });
      message.success(`Anexo "${a.nome}" excluído`);
      await carregar();
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    }
  }

  // Antd Upload trata multiplos via beforeUpload — retorna false pra bloquear o request default
  // (a gente faz upload manual via presigned URL).
  const uploadProps: UploadProps = {
    multiple: true,
    showUploadList: false,
    beforeUpload: async (file, fileList) => {
      const ok = await uploadArquivo(file);
      // Recarrega apenas após o último arquivo
      const idx = fileList.indexOf(file);
      if (ok && idx === fileList.length - 1) await carregar();
      return false; // não envia pelo Antd
    },
  };

  const columns: ColumnsType<Anexo> = [
    { title: '', dataIndex: 'tipo_arquivo', key: 'icon', width: 40, align: 'center',
      render: (v) => <span style={{ fontSize: 18 }}>{ICONES_TIPO[v] || ICONES_TIPO.other}</span> },
    { title: 'Nome', dataIndex: 'nome', key: 'nome',
      ellipsis: { showTitle: false },
      render: (v, a) => (
        <div style={{ overflow: 'hidden' }}>
          <a onClick={() => baixar(a)} title={v}
            style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
            {v}
          </a>
          {a.descricao && (
            <div style={{ fontSize: 10, color: '#888', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {a.descricao}
            </div>
          )}
        </div>
      ) },
    { title: 'Tamanho', dataIndex: 'tamanho_bytes', key: 'tamanho', width: 100, align: 'right',
      render: (v) => <Typography.Text type="secondary">{fmtBytes(v)}</Typography.Text> },
    { title: 'Enviado', dataIndex: 'criado_em', key: 'enviado', width: 140,
      render: (v, a) => (
        <div style={{ fontSize: 11 }}>
          <div>{fmtDate(v)}</div>
          {a.criado_por && (
            <Typography.Text type="secondary" style={{ fontSize: 10 }}>
              {a.criado_por.split('@')[0]}
            </Typography.Text>
          )}
        </div>
      ) },
    { title: 'Ações', key: 'acoes', width: 110, align: 'center',
      render: (_, a) => (
        <Space size={4}>
          <Button size="small" icon={<DownloadOutlined />} title="Baixar"
            onClick={() => baixar(a)} />
          <Popconfirm title={`Excluir "${a.nome}"?`} okText="Excluir" okType="danger" cancelText="Cancelar"
            onConfirm={() => excluir(a)}>
            <Button size="small" danger icon={<DeleteOutlined />} title="Excluir" />
          </Popconfirm>
        </Space>
      ) },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
        <Space size={10}>
          <Typography.Text strong style={{ color: '#020D7F' }}>
            {anexos.length} anexo{anexos.length === 1 ? '' : 's'}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            PDFs, KMZ/KML, contratos, briefings, fotos do local
          </Typography.Text>
        </Space>
        <Upload {...uploadProps}>
          <Button type="primary" icon={<PaperClipOutlined />} loading={!!enviando}>
            {enviando ? `Enviando ${enviando}…` : 'Enviar arquivos'}
          </Button>
        </Upload>
      </Space>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : anexos.length === 0 ? (
        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">Clique ou arraste arquivos pra enviar</p>
          <p className="ant-upload-hint" style={{ fontSize: 11 }}>
            PDFs, KMZ, contratos, briefings deste negócio (até 50MB cada)
          </p>
        </Upload.Dragger>
      ) : (
        <Table rowKey="id" columns={columns} dataSource={anexos} pagination={false}
          size="small" tableLayout="fixed" />
      )}
    </div>
  );
}
